import React, { Component } from 'react';
import {
    DndContext,
    closestCenter,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    SortableContext,
    arrayMove,
    rectSortingStrategy,
    useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { connect } from 'react-redux';
import { fetchPublicacion, editPublicacion, setImagesUploader } from '../../../../actions/publicacionesActions';
import { toast } from '../../../../utilities/appToast';
import PropTypes from 'prop-types';

const SortableImage = ({ id, image, onDelete }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        margin: '10px',
        opacity: isDragging ? 0.5 : 1,
        cursor: 'grab',
    };

    return (
        <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
            <div style={{ margin: '10px' }}>
                <img
                    src={image.urlImagen}
                    alt={image.name}
                    style={{
                        width: '250px',
                        height: 'auto',
                        maxHeight: '250px',
                        objectFit: 'contain',
                        marginRight: '10px',
                    }}
                />
            </div>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
                <button
                    className="btn btn-light"
                    onPointerDown={(e) => e.stopPropagation()}
                    onClick={(e) => {
                        e.preventDefault();
                        onDelete();
                    }}
                    style={{ width: '40px', height: '40px' }}
                >
                    <i className="fas fa-trash-alt" style={{ pointerEvents: 'none' }} />
                </button>
            </div>
        </div>
    );
};

SortableImage.propTypes = {
    id: PropTypes.string.isRequired,
    image: PropTypes.shape({
        urlImagen: PropTypes.string,
        name: PropTypes.string,
    }).isRequired,
    onDelete: PropTypes.func.isRequired,
};

const SortableImageList = ({ items, onSortEnd, onDelete }) => {
    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
    );

    const ids = (items || []).map((img, idx) => img.urlImagen || `item-${idx}`);

    const handleDragEnd = (event) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        const oldIndex = ids.indexOf(active.id);
        const newIndex = ids.indexOf(over.id);
        if (oldIndex === -1 || newIndex === -1) return;
        onSortEnd({ oldIndex, newIndex });
    };

    return (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={ids} strategy={rectSortingStrategy}>
                <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                    {(items || []).map((image, index) => {
                        image.posicion = index;
                        const id = image.urlImagen || `item-${index}`;
                        return (
                            <SortableImage
                                key={id}
                                id={id}
                                image={image}
                                onDelete={() => onDelete(image)}
                            />
                        );
                    })}
                </div>
            </SortableContext>
        </DndContext>
    );
};

SortableImageList.propTypes = {
    items: PropTypes.array,
    onSortEnd: PropTypes.func.isRequired,
    onDelete: PropTypes.func.isRequired,
};

class ImageUploader extends Component {
    constructor(props) {
        super(props);
        this.state = {
            images: [],
        };
    }

    componentDidMount() {
        if (!this.state.images.length) {
            this.setState({ images: this.props.publicacion.imagenes || [] });
        }
    }

    handleImageUpload = (event) => {
        const files = Array.from(event.target.files);
        const image = event.target.files[0];

        if (image.size > 512000) {
            toast.error("Tamaño de imagen debe ser menor a 500kb");
            return;
        }

        const newImages = files.map((file) => ({
            urlImagen: URL.createObjectURL(file),
            name: file.name,
            posicion: (this.state.images || []).length,
            imagen: file,
        }));
        this.setState(
            (prevState) => ({
                images: [...(prevState.images || []), ...newImages],
            }),
            () => {
                this.props.setImagesUploader(this.state.images, true);
            }
        );
    };

    onSortEnd = ({ oldIndex, newIndex }) => {
        this.setState(
            (prevState) => ({
                images: arrayMove(prevState.images || [], oldIndex, newIndex),
            }),
            () => {
                this.props.setImagesUploader(this.state.images, true);
            }
        );
    };

    handleDeleteImage = (imageToDelete) => {
        URL.revokeObjectURL(imageToDelete.urlImagen);

        this.setState(
            (prevState) => ({
                images: (prevState.images || []).filter((image) => image !== imageToDelete),
            }),
            () => {
                this.props.setImagesUploader(this.state.images, true);
            }
        );
    };

    render() {
        const { images } = this.state;

        return (
            <div>
                <input
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={this.handleImageUpload}
                    style={{ marginBottom: '20px' }}
                />
                <SortableImageList
                    items={images}
                    onSortEnd={this.onSortEnd}
                    onDelete={this.handleDeleteImage}
                />
            </div>
        );
    }
}

ImageUploader.propTypes = {
    publicacion: PropTypes.shape({
        imagenes: PropTypes.array,
        idPublicacion: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
    setImagesUploader: PropTypes.func,
};

const mapStateToProps = (state) => ({
    publicacion: state.publicaciones.publicacion,
    userId: state.general.userId,
    imagesUploader: state.publicaciones.imagesUploader,
});

export default connect(mapStateToProps, { fetchPublicacion, editPublicacion, setImagesUploader })(ImageUploader);
